import { useRef, useState } from "react";

import { Button } from "@theorgchartdev/storybook";
import { LabelWrapper } from "@theorgchartdev/storybook";
import { QBambooCallbackRFC } from "@coreapp/CoreApp/QBambooCallbackRFC";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import { DATA_SOURCES_TEST_PREFIX } from "../Utils";

type AuthPayload = {
  code: string;
  state: string;
};

const isAuthPayload = (payload: any): payload is AuthPayload => {
  return typeof payload?.code === "string" && typeof payload?.state === "string";
};

type Props = {
  value: Partial<{
    apiKey: string;
    subdomain: string;
  }>;
  onChange: (key: string, value: any) => void;
};

export const BambooOIDCSetup = (props: Props) => {
  const [hasOpenPopup, setPopupOpen] = useState(false);
  const [hasAuthError, setAuthError] = useState(false);
  const [isAuthenticating, setAuthenticating] = useState(false);

  const channelRef = useRef(new BroadcastChannel("oidc"));

  const handleBroadcastMessage = async (event: MessageEvent) => {
    try {
      setAuthenticating(true);
      setAuthError(false);
      const { data } = event;
      if (!isAuthPayload(data)) {
        throw new Error();
      }

      const callbackRFC = new QBambooCallbackRFC();
      callbackRFC.InitRFC();
      callbackRFC.SetParams(props.value.subdomain ?? "", data.code, data.state);
      const responsePromise = callbackRFC.GetPromise();
      Globals._rfcMgr?.QueueRFC(callbackRFC);

      const response = await responsePromise;
      const result = JSON.parse(response);
      if (!result["success"]) {
        throw new Error();
      }

      props.onChange("apiKey", result.data.apiKey);
    } catch {
      setAuthError(true);
    } finally {
      setAuthenticating(false);
    }
  };

  const handleAuthenticate = () => {
    const subdomain = props.value.subdomain;
    if (typeof subdomain !== "string" || subdomain.length === 0) {
      return;
    }

    setPopupOpen(true);
    channelRef.current.addEventListener("message", handleBroadcastMessage);

    const bambooURL = new URL(
      `${document.location.protocol}//${document.location.host}/ServerSide/endpoints/qbambooredirect`
    );
    bambooURL.searchParams.append("subdomain", subdomain);

    const popup = window.open(bambooURL, "popup", "popup=true");

    // Poll for the popup to be closed by user
    const intervalId = setInterval(() => {
      if (popup?.closed) {
        setPopupOpen(false);
        clearInterval(intervalId);
      }
    }, 1000);
  };

  const hasValidKey = typeof props.value.apiKey === "string";
  const subdomain = props.value.subdomain;
  const hasValidSubdomain = typeof subdomain === "string" && subdomain.length > 0;

  return (
    <LabelWrapper message={hasAuthError ? appstr("loginfail") : undefined} status={"error"}>
      <Button
        dataTestId={`${DATA_SOURCES_TEST_PREFIX}-bamboohr-oidc-authenticate-button`}
        isFullWidth
        modifier={"secondary"}
        loading={isAuthenticating || hasOpenPopup}
        disabled={!hasValidSubdomain}
        onClick={handleAuthenticate}
        color={hasValidKey ? "success" : undefined}
        iconProps={
          hasValidKey
            ? {
                name: "check"
              }
            : undefined
        }
      >
        {appstr("Log in")}
      </Button>
    </LabelWrapper>
  );
};
